import React, { useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {
  BusinessType,
  BusinessTypeNamingMapping,
  getCompanyAddressSchema,
  getCompanyCurrencySchema,
  sanitizeCompanyAddressForSave,
  SUPPORTED_COMPANY_COUNTRIES,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useUpdateCompany } from "../../hooks/api/useCompany";
import { CompanyInfo } from "../Registration/types/registrationTypes";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";
import { CompanyEditFormModalProps } from "../Profile/types/profileTypes";
import CompanyVatFields from "./CompanyVatFields";
import { withCompanyVatDefaults } from "./constants/companyVatConstants";

import CompanyCountryCurrencyAddressSection from "./address/CompanyCountryCurrencyAddressSection";
import { useOrganizationLabels } from "../../hooks/useOrganizationLabels";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const CompanyEditFormModal: React.FC<CompanyEditFormModalProps> = ({
  open,
  onClose
}) => {
  const { t } = useKivunovaTranslation();
  const org = useOrganizationLabels();
  const companyData = useSelector(
    (state: RootState) => state.profile.companyData
  );
  const profileData = useSelector(
    (state: RootState) => state.profile.profileData
  );

  const updateCompany = useUpdateCompany();

  const schema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(org.nameRequired),
        businessType: Yup.string().required(
          t("business-type-required", "Business type is required")
        ),
        currencyCode: getCompanyCurrencySchema(t),
        registrationNumber: Yup.string().required(
          t("registration-number-required", "Registration number is required")
        ),
        description: Yup.string().required(
          t("description-required", "Description is required")
        ),
        vatPercentage: Yup.number().required(
          t("vat-percentage-required", "VAT percentage is required")
        ),
        vatCalculationMode: Yup.string().required(
          t("vat-calculation-mode-required", "VAT calculation mode is required")
        ),
        address: getCompanyAddressSchema(t)
      }).required(),
    [t, org.nameRequired]
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty }
  } = useForm<CompanyInfo>({
    resolver: yupResolver(schema) as never,
    mode: "onChange",
    defaultValues: companyData ? withCompanyVatDefaults(companyData) : undefined
  });

  useEffect(() => {
    if (open && companyData) {
      reset(withCompanyVatDefaults(companyData));
    }
  }, [open, companyData, reset]);

  const onSubmit = useCallback(
    async (data: CompanyInfo) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "CompanyEdit: Starting to update company details",
        buildLogContext(
          "company-profile",
          "update-company-details",
          profileData?.id,
          profileData?.companyId,
          undefined,
          {
            feature: "company-edit",
            action: "click-submit"
          }
        )
      );

      if (!profileData?.companyId) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "CompanyEdit: Missing companyId when updating company details",
          buildLogContext(
            "company-profile",
            "edit-company-details",
            profileData?.id,
            profileData?.companyId,
            undefined,
            {
              feature: "company-edit",
              action: "submit"
            }
          )
        );
        return;
      }

      const updates = {
        companyId: profileData.companyId,
        updates: {
          ...data,
          address: sanitizeCompanyAddressForSave(data.address)
        }
      };

      try {
        await updateCompany.mutateAsync(updates);
        reset(data);
        onClose();

        sendAppLog(
          LOG_LEVEL.INFO,
          "CompanyEdit: Successfully updated company details",
          buildLogContext(
            "company-profile",
            "edit-company-data",
            profileData?.id,
            profileData?.companyId,
            undefined,
            {
              feature: "company-edit",
              action: "click-submit"
            }
          )
        );
      } catch (err) {
        captureAppException(
          "CompanyEdit: Failed to update company details",
          err,
          buildLogContext(
            "company-profile",
            "updating-company-details",
            profileData?.id,
            profileData?.companyId,
            undefined,
            {
              feature: "company-edit",
              action: "update-company"
            }
          )
        );
      }
    },
    [updateCompany, profileData?.companyId, profileData?.id, reset, onClose]
  );

  const handleCancel = useCallback(() => {
    reset(companyData ? withCompanyVatDefaults(companyData) : undefined);
    onClose();
  }, [reset, onClose, companyData]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="company-edit-modal-title"
      aria-describedby="company-edit-modal-description"
    >
      <Box className="modal-styles" data-testid="company-edit-modal-content">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="textPrimary">
            {org.editDetailsTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          data-testid="company-edit-form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flexGrow: 1
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("name", "Name")}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            <Controller
              name="businessType"
              control={control}
              render={({ field }) => {
                const label =
                  field.value &&
                  BusinessTypeNamingMapping[field.value as BusinessType]
                    ? t(
                        `business-type-${BusinessTypeNamingMapping[field.value as BusinessType]}`,
                        BusinessTypeNamingMapping[field.value as BusinessType]
                      )
                    : field.value;

                return (
                  <TextField
                    fullWidth
                    size="small"
                    label={t("business-type-label-text", "Business Type")}
                    value={label ?? ""}
                    InputProps={{ readOnly: true }}
                    helperText={t(
                      "business-type-readonly-help",
                      "Business type cannot be changed after registration."
                    )}
                  />
                );
              }}
            />

            <Controller
              name="registrationNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={t("registration-number", "Registration Number")}
                  error={!!errors.registrationNumber}
                  helperText={errors.registrationNumber?.message}
                />
              )}
            />
          </Box>

          <CompanyCountryCurrencyAddressSection
            control={control as never}
            errors={errors}
            watch={watch as never}
            setValue={setValue as never}
            addressFieldPrefix="address"
            currencyFieldName="currencyCode"
            countryEditable={false}
            availableCountries={SUPPORTED_COMPANY_COUNTRIES}
            selectSx={undefined}
            inputSx={undefined}
            size="small"
          />

          <CompanyVatFields
            control={control}
            vatPercentageName="vatPercentage"
            vatCalculationModeName="vatCalculationMode"
            selectSize="small"
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t("description", "Description")}
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />

          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 2,
              flexShrink: 0
            }}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              loading={updateCompany.isPending}
              disabled={!isDirty}
              fullWidth
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: BRAND_COLOR,
                "&:hover": { backgroundColor: BRAND_HOVER }
              }}
            >
              {t("save-changes", "Save Changes")}
            </LoadingButton>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              disabled={updateCompany.isPending}
              fullWidth
              sx={{
                textTransform: "none",
                borderRadius: 2,
                backgroundColor: "white",
                color: BRAND_COLOR,
                border: `1px solid ${BRAND_COLOR}`,
                "&:hover": {
                  backgroundColor: BRAND_HOVER,
                  color: "white",
                  borderColor: BRAND_HOVER
                }
              }}
            >
              {t("cancel", "Cancel")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CompanyEditFormModal;
